import { NavLink, Link, useNavigate, Outlet } from 'react-router-dom'
import { useState } from 'react'
import { signOut } from 'firebase/auth'
import { auth } from '../lib/firebase'
import {
  Home,
  TrendingUp,
  Target,
  BookOpen,
  User,
  Menu,
  X,
  LogOut,
  LogIn,
  UserPlus,
  BarChart3,
  Trophy,
  Activity
} from 'lucide-react'

const Layout = ({ user }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const navigate = useNavigate()

  const handleSignOut = async () => {
    try {
      await signOut(auth)
      navigate('/')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  // Public navigation items (available to everyone)
  const publicNavItems = [
    { path: '/games', label: 'Games', icon: Trophy },
    { path: '/analytics', label: 'Analytics', icon: TrendingUp },
    { path: '/learn', label: 'Learn', icon: BookOpen },
  ]

  // Additional items for logged-in users
  const authNavItems = [
    { path: '/dashboard', label: 'My Dashboard', icon: Home },
    { path: '/profile', label: 'Profile', icon: User },
  ]

  const navItems = user ? [...publicNavItems, ...authNavItems] : publicNavItems

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-dark-card border-r border-dark-border px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-primary-500 hover:text-primary-400">
              SweePicks
            </Link>
          </div>

          {/* User Status */}
          {user ? (
            <div className="bg-gray-800/50 rounded-lg p-3">
              <p className="text-xs text-gray-400">Signed in as</p>
              <p className="text-sm font-medium text-white truncate">{user.email}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </Link>
              <Link
                to="/register"
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                Sign Up Free
              </Link>
            </div>
          )}

          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {navItems.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          `group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                            isActive
                              ? 'bg-primary-500 text-white'
                              : 'text-gray-400 hover:text-white hover:bg-gray-800'
                          }`
                        }
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
              {user && (
                <li className="mt-auto">
                  <button
                    onClick={handleSignOut}
                    className="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-400 hover:text-white hover:bg-gray-800 w-full"
                  >
                    <LogOut className="h-6 w-6 shrink-0" />
                    Sign Out
                  </button>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile header */}
      <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-dark-card border-b border-dark-border px-4 py-4 shadow-sm lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-gray-400"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        <Link to="/" className="flex-1 text-sm font-semibold leading-6 text-white">
          SweePicks Analytics
        </Link>
        {!user && (
          <Link
            to="/login"
            className="text-sm font-medium text-primary-500 hover:text-primary-400"
          >
            Sign In
          </Link>
        )}
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/80" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 w-72 bg-dark-card px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              <Link to="/" className="text-2xl font-bold text-primary-500">
                SweePicks
              </Link>
            </div>

            {/* Mobile User Status */}
            {user ? (
              <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-400">Signed in as</p>
                <p className="text-sm font-medium text-white truncate">{user.email}</p>
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  <UserPlus className="h-4 w-4" />
                  Sign Up Free
                </Link>
              </div>
            )}

            <nav className="flex flex-1 flex-col">
              <ul className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul className="-mx-2 space-y-1">
                    {navItems.map((item) => (
                      <li key={item.path}>
                        <NavLink
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={({ isActive }) =>
                            `group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                              isActive
                                ? 'bg-primary-500 text-white'
                                : 'text-gray-400 hover:text-white hover:bg-gray-800'
                            }`
                          }
                        >
                          <item.icon className="h-6 w-6 shrink-0" />
                          {item.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
                {user && (
                  <li className="mt-auto">
                    <button
                      onClick={handleSignOut}
                      className="group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-400 hover:text-white hover:bg-gray-800 w-full"
                    >
                      <LogOut className="h-6 w-6 shrink-0" />
                      Sign Out
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="lg:pl-72">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

export default Layout